import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ContactCenterApiService } from '../../../v2/apis/contact-center/contact-center-api.service';

export interface AssignTicketToDialogData {
    ticketId: number | string;
    currentAssignee?: string;
    currentEta?: string;
    currentDevTeam?: string;
    referenceType?: string;
}

@Component({
    selector: 'app-assign-ticket-to-dialog',
    templateUrl: './assign-ticket-to-dialog.component.html',
    styleUrls: ['./assign-ticket-to-dialog.component.scss'],
    standalone: false
})
export class AssignTicketToDialogComponent implements OnInit {
    assignForm = new FormGroup({
        assignee: new FormControl(''),
        eta: new FormControl(''),
        devTeam: new FormControl('')
    });

    userList: any[] = [];
    filteredUsers: any[] = [];
    categories: string[] = [];
    filteredCategories: string[] = [];
    searchControl = new FormControl('');
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    showDropdown: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<AssignTicketToDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AssignTicketToDialogData,
        private contactCenterService: ContactCenterApiService,
        private toastr: ToastrService
    ) {
        if (data) {
            this.assignForm.patchValue({
                assignee: data.currentAssignee || '',
                eta: data.currentEta || '',
                devTeam: data.currentDevTeam || ''
            });
        }
    }

    ngOnInit(): void {
        this.loadCategories();
        this.loadAssignmentHistory();
        this.assignForm.controls.assignee.valueChanges.subscribe(value => {
            this.filterUsers(value || '');
            this.showDropdown = true;
        });
        this.searchControl.valueChanges.subscribe(value => {
            this.filterCategories(value || '');
        });
    }

    loadCategories(): void {
        this.contactCenterService.getAvailableCategories().subscribe({
            next: (response) => {
                this.categories = response?.categories || [];
                this.filteredCategories = [...this.categories];
            },
            error: () => {
                this.categories = [];
                this.filteredCategories = [];
            }
        });
    }

    loadAssignmentHistory(): void {
        const passedAssignee = this.data.currentAssignee?.trim();
        if (passedAssignee && passedAssignee.toLowerCase() !== 'unassigned') {
            this.currentAssignedCategory = passedAssignee;
            this.assignForm.controls.assignee.setValue(passedAssignee);
            return;
        }
        const referenceId = Number(this.data.ticketId);
        const referenceType = this.data.referenceType || 'feedback';
        this.contactCenterService.getAssignmentHistory(referenceId, referenceType).subscribe({
            next: (history: any[]) => {
                if (Array.isArray(history) && history.length > 0) {
                    const latest = history[history.length - 1];
                    if (latest.assigned_to_category) {
                        this.currentAssignedCategory = latest.assigned_to_category;
                        this.assignForm.controls.assignee.setValue(latest.assigned_to_category);
                    }
                } else {
                    this.currentAssignedCategory = 'SUPERUSER';
                    this.assignForm.controls.assignee.setValue('SUPERUSER');
                }
            },
            error: () => {
                this.currentAssignedCategory = 'SUPERUSER';
                this.assignForm.controls.assignee.setValue('SUPERUSER');
            }
        });
    }

    filterCategories(searchTerm: string): void {
        if (!searchTerm) {
            this.filteredCategories = [...this.categories];
            return;
        }
        const term = searchTerm.toLowerCase();
        this.filteredCategories = this.categories.filter(cat => cat.toLowerCase().includes(term));
    }

    currentAssignedCategory: string = 'SUPERUSER';

    toggleCategory(category: string): void {
        if (this.currentAssignedCategory !== category) {
            this.assignCategory(category);
        }
    }

    assignCategory(category: string): void {
        const payload = {
            reference_id: Number(this.data.ticketId),
            reference_type: this.data.referenceType || 'feedback',
            assigned_to_category: category,
            assigned_by_wwid: Number(sessionStorage.getItem('userId')),
            comment: '',
            business_unit_code: sessionStorage.getItem('businessUnitCode') || '',
            region: ''
        };

        this.contactCenterService.createContactCenterAssignment(payload).subscribe({
            next: () => {
                this.currentAssignedCategory = category;
                this.assignForm.controls.assignee.setValue(category);
                this.toastr.success(`Assigned to ${category} successfully.`);
                this.dialogRef.close({ assigned_category: category });
            },
            error: () => {
                this.toastr.error(`Failed to assign to ${category}.`);
            }
        });
    }

    unassignCategory(category: string): void {
        const payload = {
            reference_id: Number(this.data.ticketId),
            reference_type: this.data.referenceType || 'feedback',
            assigned_to_category: category,
            assigned_by_wwid: Number(sessionStorage.getItem('userId')),
            comment: 'unassigned',
            business_unit_code: sessionStorage.getItem('businessUnitCode') || '',
            region: ''
        };

        this.contactCenterService.createContactCenterAssignment(payload).subscribe({
            next: () => {
                this.currentAssignedCategory = '';
                this.assignForm.controls.assignee.setValue('');
                this.toastr.success(`Unassigned from ${category} successfully.`);
                this.dialogRef.close({ assigned_category: '' });
            },
            error: () => {
                this.toastr.error(`Failed to unassign from ${category}.`);
            }
        });
    }

    loadUsers(): void {
        this.isLoading = true;
        const payload = {
            reference_id: 0,
            reference_type: this.data.referenceType || 'feedback',
            assigned_to_category: '',
            assigned_by_wwid: Number(sessionStorage.getItem('userId')),
            comment: '',
            business_unit_code: sessionStorage.getItem('businessUnitCode') || '',
            region: ''
        };
        this.contactCenterService.createContactCenterAssignment(payload).subscribe({
            next: (response) => {
                this.userList = Array.isArray(response) ? response : (response?.data || response?.users || []);
                this.filteredUsers = [...this.userList];
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    filterUsers(searchTerm: string): void {
        if (!searchTerm) {
            this.filteredUsers = [...this.userList];
            return;
        }
        const term = searchTerm.toLowerCase();
        this.filteredUsers = this.userList.filter(user =>
            (user.name || user.first_name || user.given_name || '').toLowerCase().includes(term) ||
            (user.email || '').toLowerCase().includes(term)
        );
    }

    selectUser(user: any): void {
        const displayName = user.name || user.first_name || user.given_name || user.email || '';
        this.assignForm.controls.assignee.setValue(displayName);
        this.showDropdown = false;
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onAssign(): void {
        const lastAssigned = this.assignForm.controls.assignee.value?.trim();
        this.dialogRef.close({
            assigned_category: lastAssigned || ''
        });
    }
}
